import React, { useState, useEffect } from "react";
import { Container, Row, Col, Tab, Nav, Button, Badge, Spinner, ProgressBar, Modal } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import moment from "moment";

// Images
import homeImage from "../../assets/images/home.svg";
import bin from "../../assets/images/bin.png";

// Components
import { DatabaseDetailSkeleton } from "./DatabaseSkeleton";

// Redux
import {
    DatabaseGetById,
    DatabaseGetMonitoringAll,
    DatabaseDelete,
    DatabaseStart,
    DatabaseStop,
    DatabaseRestart,
    clearCurrentDatabase,
} from "../../services/ReduxTookit/Slice/DatabaseRedux";

// Database Icon Component
const DatabaseIcon = ({ type = "postgresql", size = 48 }) => {
    const colors = {
        postgresql: "#336791",
        mysql: "#00618a",
        mongodb: "#47a248",
        redis: "#dc382d",
        mssql: "#cc292b",
        sqlserver: "#cc292b",
        mariadb: "#c07158",
    };
    const color = colors[type] || colors.postgresql;

    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
            <ellipse cx="12" cy="5" rx="9" ry="3" fill={color} />
            <path d="M3 5v7c0 1.66 4.03 3 9 3s9-1.34 9-3V5" stroke={color} strokeWidth="1.5" fill="none" />
            <path d="M3 12v7c0 1.66 4.03 3 9 3s9-1.34 9-3v-7" stroke={color} strokeWidth="1.5" fill="none" />
        </svg>
    );
};

// Copy Icon
const CopyIcon = ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
        <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
    </svg>
);

// Animated dots component for loading states - cycles through . .. ...
const AnimatedDots = () => (
    <span className="animated-dots-cycle"></span>
);

// Status Badge Component
const StatusBadge = ({ status }) => {
    const config = {
        running: { variant: "success", icon: "●", label: "Running" },
        stopped: { variant: "danger", icon: "■", label: "Stopped" },
        starting: { variant: "warning", icon: "◐", label: "Starting", animated: true },
        stopping: { variant: "warning", icon: "◐", label: "Stopping", animated: true },
        restarting: { variant: "warning", icon: "◐", label: "Restarting", animated: true },
        creating: { variant: "info", icon: "◐", label: "Creating", animated: true },
        pending: { variant: "secondary", icon: "○", label: "Pending", animated: true },
        error: { variant: "danger", icon: "✕", label: "Error" },
    };
    const { variant, icon, label, animated } = config[status] || config.pending;

    return (
        <Badge bg={variant} className="db-status-badge">
            <span className="status-icon">{icon}</span> {label}
            {animated && <AnimatedDots />}
        </Badge>
    );
};

// Metric Card Component
const MetricCard = ({ icon, title, value, unit, subtitle, percent, color = "primary" }) => (
    <div className="db-detail-metric-card">
        <div className="metric-header">
            <span className={`metric-icon text-${color}`}>{icon}</span>
            <span className="metric-title">{title}</span>
        </div>
        <div className="metric-body">
            <span className="metric-value">{value}</span>
            <span className="metric-unit">{unit}</span>
        </div>
        {subtitle && <div className="metric-subtitle">{subtitle}</div>}
        {percent !== undefined && (
            <ProgressBar now={percent} variant={color} className="metric-progress" />
        )}
    </div>
);

// Info Table Row Component
const InfoRow = ({ label, value, copyable = false, onCopy }) => (
    <tr>
        <td className="info-label">{label}</td>
        <td className="info-value">
            {copyable ? (
                <div className="copyable-value">
                    <code>{value}</code>
                    <button className="copy-btn-sm" onClick={() => onCopy(value)} title="Copy">
                        <CopyIcon size={14} />
                    </button>
                </div>
            ) : (
                value
            )}
        </td>
    </tr>
);

const defaultPorts = {
    postgresql: 5432,
    mysql: 3306,
    mongodb: 27017,
    redis: 6379,
    mssql: 1433,
    sqlserver: 1433,
    mariadb: 3306,
};

const getEngineDisplayName = (type) => {
    const t = (type || "").toLowerCase();
    if (t === "sqlserver" || t === "mssql") return "SQL Server";
    if (t === "mysql") return "MySQL";
    if (t === "mariadb") return "MariaDB";
    if (t === "mongodb") return "MongoDB";
    if (t === "redis") return "Redis";
    return "PostgreSQL";
};

const buildConnectionString = (type, host, port, database, username, ssl = false) => {
    const t = (type || "postgresql").toLowerCase();
    const p = port || defaultPorts[t] || 5432;
    const h = host || "localhost";
    const u = username || "user";
    const d = database || "appdb";

    if (t === "sqlserver" || t === "mssql") {
        return `Server=${h},${p};Database=${d};User Id=${u};${ssl ? "Encrypt=True;" : ""}TrustServerCertificate=True;`;
    }
    if (t === "mysql") {
        return `mysql://${u}@${h}:${p}/${d}${ssl ? "?ssl=true" : ""}`;
    }
    if (t === "mariadb") {
        return `mariadb://${u}@${h}:${p}/${d}${ssl ? "?ssl=true" : ""}`;
    }
    if (t === "mongodb") {
        return `mongodb://${encodeURIComponent(u)}@${h}:${p}/${d}?authSource=admin${ssl ? "&ssl=true" : ""}`;
    }
    if (t === "redis") {
        return `redis://${h}:${p}`;
    }
    return `postgresql://${u}@${h}:${p}/${d}${ssl ? "?sslmode=require" : ""}`;
};

function DatabaseDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    // State
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState("overview");
    const [isActionLoading, setIsActionLoading] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [deletePassword, setDeletePassword] = useState("");
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [showStopModal, setShowStopModal] = useState(false);

    // Redux state
    const { currentDatabase, currentMetrics, currentConnections, currentStatus } = useSelector(
        (state) => state.databaseredux
    );

    // Build database object from Redux state
    const db = currentDatabase || {};
    const metrics = currentMetrics || {};
    const connections = currentConnections || {};

    const endpointHost = db.endpoints?.production?.host || db.endpoint?.split(":")[0] || "pending";
    const endpointPort = db.endpoints?.production?.port || db.port || defaultPorts[db.type?.toLowerCase()] || 5432;

    const database = {
        ...db,
        type: db.type || "postgresql",
        version: db.version || "16",
        database: db.database || "appdb",
        storage: db.storage || "20Gi",
        maxConnections: db.maxConnections || 100,
        environment: db.environment || "development",
        status: currentStatus?.status || db.status || "unknown",
        endpoints: db.endpoints || {},
        connectionString: db.connectionString || buildConnectionString(db.type, endpointHost, endpointPort, db.database, db.username, false),
        sslConnectionString: db.sslConnectionString || buildConnectionString(db.type, endpointHost, endpointPort, db.database, db.username, true),
        cpuCores: db.cpuCores || 1,
        ramGb: db.ramGb || 2,
        metrics: {
            cpu: { percent: parseFloat(metrics.cpu || metrics.cpuPercent || "0"), cores: db.cpuCores || 1 },
            memory: {
                percent: parseFloat(metrics.memory || metrics.memoryPercent || "0"),
                used_gb: parseFloat(metrics.memoryUsage || 0) / (1024 * 1024 * 1024) || 0,
                total_gb: parseFloat(metrics.memoryLimit || 0) / (1024 * 1024 * 1024) || db.ramGb || 2,
            },
            disk: {
                percent: parseFloat(metrics.disk || metrics.diskPercent || "0"),
                used_gb: parseFloat(metrics.diskUsage || 0) / (1024 * 1024 * 1024) || 0,
                total_gb: parseFloat(metrics.diskTotal || 0) / (1024 * 1024 * 1024) || 20,
            },
            connections: {
                active: connections.active || metrics.activeConnections || 0,
                idle: connections.idle || 0,
                total: connections.total || 0,
                max: connections.max || db.maxConnections || 100,
            },
            uptime: "N/A",
            lastBackup: null,
        },
        k8sNamespace: db.k8sNamespace || "",
        podName: db.podName || "",
        serviceName: db.serviceName || "",
        backupEnabled: false,
        backupSchedule: "Not configured",
        backupRetention: "N/A",
    };

    // Check if database is in transitional state
    const isTransitionalStatus = ["starting", "restarting", "stopping", "creating", "pending"].includes(database.status);

    // Fetch data on mount
    useEffect(() => {
        if (id) {
            setLoading(true);
            Promise.all([
                dispatch(DatabaseGetById(id)),
                dispatch(DatabaseGetMonitoringAll(id)),
            ]).then(() => {
                setLoading(false);
            }).catch(() => {
                setLoading(false);
            });
        }

        return () => {
            dispatch(clearCurrentDatabase());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // Poll for status updates when database is in transitional state
    useEffect(() => {
        let pollInterval;

        if (isTransitionalStatus && !loading) {
            pollInterval = setInterval(() => {
                dispatch(DatabaseGetById(id));
            }, 5000); // Poll every 5 seconds
        }

        return () => {
            if (pollInterval) {
                clearInterval(pollInterval);
            }
        };
    }, [isTransitionalStatus, loading, dispatch, id]);

    // Copy to clipboard
    const handleCopy = (text) => {
        navigator.clipboard.writeText(text);
        toast.success("Copied to clipboard!");
    };

    // Action handlers - real API
    const handleStart = async () => {
        setIsActionLoading(true);
        const result = await dispatch(DatabaseStart(id));
        if (result?.payload?.isValidate) {
            toast.success("Database is starting...");
            // Refresh immediately to get "starting" status
            dispatch(DatabaseGetById(id));
        }
        setIsActionLoading(false);
    };

    const handleStop = () => {
        setShowStopModal(true);
    };

    const handleConfirmStop = async () => {
        setShowStopModal(false);
        setIsActionLoading(true);
        const result = await dispatch(DatabaseStop(id));
        if (result?.payload?.isValidate) {
            toast.success("Database is stopping...");
            dispatch(DatabaseGetById(id));
        }
        setIsActionLoading(false);
    };

    const handleRestart = async () => {
        setIsActionLoading(true);
        const result = await dispatch(DatabaseRestart(id));
        if (result?.payload?.isValidate) {
            toast.success("Database is restarting...");
            dispatch(DatabaseGetById(id));
        }
        setIsActionLoading(false);
    };

    const handleDelete = async () => {
        setShowDeleteModal(false);
        // If production, show password modal
        if (database.environment === "production") {
            setShowPasswordModal(true);
            return;
        }
        await performDelete();
    };

    const handlePasswordDelete = async () => {
        if (!deletePassword.trim()) {
            toast.error("Password is required for production databases");
            return;
        }
        await performDelete(deletePassword);
    };

    const performDelete = async (password) => {
        setDeleteLoading(true);
        const result = await dispatch(DatabaseDelete({ id, password }));
        setDeleteLoading(false);
        if (result?.payload?.isValidate) {
            toast.success("Database deleted");
            navigate("/database");
        } else {
            toast.error(result?.payload?.message || "Failed to delete database");
        }
        setShowPasswordModal(false);
        setDeletePassword("");
    };

    const handleRefresh = () => {
        setLoading(true);
        Promise.all([
            dispatch(DatabaseGetById(id)),
            dispatch(DatabaseGetMonitoringAll(id)),
        ]).then(() => {
            setLoading(false);
            toast.info("Data refreshed");
        }).catch(() => {
            setLoading(false);
        });
    };

    if (loading) {
        return (
            <div className="db-detail-page database-page-main schedular-page-main all-page-top">
                <Container>
                    {/* Breadcrumb */}
                    <div className="top-bot-section flex-column align-items-start gap-3">
                        <div className="d-flex justify-content-between align-items-center w-100">
                            <h6>
                                <img
                                    src={homeImage}
                                    alt="home"
                                    title="home"
                                    style={{ cursor: "pointer", marginBottom: "3px" }}
                                    onClick={() => navigate("/")}
                                />
                                <span style={{ fontWeight: "400", color: "#666666" }}>{" | "}</span>
                                <span
                                    className="mt-1"
                                    style={{ cursor: "pointer" }}
                                    onClick={() => navigate("/database")}
                                >
                                    Database Instances
                                </span>
                                <span style={{ fontWeight: "400", color: "#666666" }}>{" / "}</span>
                                <span className="mt-1">Loading...</span>
                            </h6>
                        </div>
                    </div>
                </Container>
                <div className="main-page-content">
                    <Container>
                        <DatabaseDetailSkeleton />
                    </Container>
                </div>
            </div>
        );
    }

    return (
        <div className="db-detail-page database-page-main schedular-page-main all-page-top">
            <Container>
                {/* Breadcrumb */}
                <div className="top-bot-section flex-column align-items-start gap-3">
                    <div className="d-flex justify-content-between align-items-center w-100">
                        <h6>
                            <img
                                src={homeImage}
                                alt="home"
                                style={{ cursor: "pointer", marginBottom: "3px" }}
                                onClick={() => navigate("/")}
                            />
                            <span style={{ fontWeight: "400", color: "#666666" }}>{" | "}</span>
                            <span
                                onClick={() => navigate("/database")}
                                style={{ cursor: "pointer", color: "#666666" }}
                            >
                                Database Instances
                            </span>
                            <span style={{ fontWeight: "400", color: "#666666" }}>{" | "}</span>
                            <span className="mt-1">{database.name}</span>
                        </h6>
                    </div>
                </div>
            </Container>

            {/* Main Content */}
            <div className="main-page-content">
                <Container>
                    {/* Header Card */}
                    <div className="db-detail-header">
                        <div className="db-detail-header-left">
                            <div className="db-detail-icon">
                                <DatabaseIcon type={database.type} size={56} />
                            </div>
                            <div className="db-detail-info">
                                <h1 className="db-detail-name">{database.name}</h1>
                                <div className="db-detail-meta">
                                    <span className="db-engine">
                                        {database.type.toUpperCase()} {database.version}
                                    </span>
                                    <span className="meta-divider">•</span>
                                    <StatusBadge status={database.status} />
                                </div>
                            </div>
                        </div>
                        <div className="db-detail-header-right">
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={handleRefresh}
                                disabled={isTransitionalStatus || isActionLoading}
                                className="action-btn btn-refresh"
                            >
                                <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                                    <path d="M13.65 2.35A7.958 7.958 0 008 0C3.58 0 .01 3.58.01 8S3.58 16 8 16c3.73 0 6.84-2.55 7.73-6h-2.08A5.99 5.99 0 018 14 6 6 0 012 8a6 6 0 016-6c1.66 0 3.14.69 4.22 1.78L9 7h7V0l-2.35 2.35z" />
                                </svg>
                                Refresh
                            </Button>
                            {database.status === "running" ? (
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={handleStop}
                                    disabled={isTransitionalStatus || isActionLoading}
                                    className="action-btn btn-stop"
                                >
                                    {isActionLoading ? (
                                        <Spinner animation="border" size="sm" />
                                    ) : (
                                        <>
                                            <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                                                <rect x="3" y="3" width="10" height="10" rx="1" />
                                            </svg>
                                            Stop
                                        </>
                                    )}
                                </Button>
                            ) : database.status === "stopped" ? (
                                <Button
                                    variant="outline-success"
                                    size="sm"
                                    onClick={handleStart}
                                    disabled={isTransitionalStatus || isActionLoading}
                                    className="action-btn"
                                >
                                    {isActionLoading ? (
                                        <Spinner animation="border" size="sm" />
                                    ) : (
                                        <>
                                            <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                                                <path d="M4 2l10 6-10 6V2z" />
                                            </svg>
                                            Start
                                        </>
                                    )}
                                </Button>
                            ) : (
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    disabled
                                    className="action-btn"
                                >
                                    <Spinner animation="border" size="sm" />
                                </Button>
                            )}
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={handleRestart}
                                disabled={isTransitionalStatus || isActionLoading || database.status !== "running"}
                                className="action-btn btn-restart"
                            >
                                <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                                    <path d="M11.534 7h3.932a.25.25 0 01.192.41l-1.966 2.36a.25.25 0 01-.384 0l-1.966-2.36a.25.25 0 01.192-.41zm-11 2h3.932a.25.25 0 00.192-.41L2.692 6.23a.25.25 0 00-.384 0L.342 8.59A.25.25 0 00.534 9z" />
                                    <path fillRule="evenodd" d="M8 3c-1.552 0-2.94.707-3.857 1.818a.5.5 0 11-.771-.636A6.002 6.002 0 0113.917 7H12.9A5.002 5.002 0 008 3zM3.1 9a5.002 5.002 0 008.757 2.182.5.5 0 11.771.636A6.002 6.002 0 012.083 9H3.1z" />
                                </svg>
                                Restart
                            </Button>
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => setShowDeleteModal(true)}
                                disabled={isTransitionalStatus || isActionLoading}
                                className="action-btn btn-delete"
                            >
                                <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                                    <path d="M5.5 0v1H1v2h14V1h-4.5V0h-5zM2 4v11a1 1 0 001 1h10a1 1 0 001-1V4H2z" />
                                </svg>
                                Delete
                            </Button>
                        </div>
                    </div>

                    {/* Quick Stats */}
                    <Row className="db-stats-row">
                        <Col xs={6} lg={3}>
                            <MetricCard
                                icon={
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                                        <rect x="4" y="4" width="16" height="16" rx="2" ry="2" />
                                        <rect x="9" y="9" width="6" height="6" />
                                        <line x1="9" y1="1" x2="9" y2="4" />
                                        <line x1="15" y1="1" x2="15" y2="4" />
                                        <line x1="9" y1="20" x2="9" y2="23" />
                                        <line x1="15" y1="20" x2="15" y2="23" />
                                        <line x1="20" y1="9" x2="23" y2="9" />
                                        <line x1="20" y1="15" x2="23" y2="15" />
                                        <line x1="1" y1="9" x2="4" y2="9" />
                                        <line x1="1" y1="15" x2="4" y2="15" />
                                    </svg>
                                }
                                title="CPU Usage"
                                value={database.metrics.cpu.percent}
                                unit="%"
                                subtitle={`${database.metrics.cpu.cores} vCPU cores`}
                                percent={database.metrics.cpu.percent}
                                color="primary"
                            />
                        </Col>
                        <Col xs={6} lg={3}>
                            <MetricCard
                                icon={
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                                        <rect x="2" y="6" width="20" height="12" rx="2" />
                                        <line x1="6" y1="10" x2="6" y2="14" />
                                        <line x1="10" y1="10" x2="10" y2="14" />
                                        <line x1="14" y1="10" x2="14" y2="14" />
                                        <line x1="18" y1="10" x2="18" y2="14" />
                                        <path d="M6 6V4" />
                                        <path d="M18 6V4" />
                                        <path d="M6 18v2" />
                                        <path d="M18 18v2" />
                                    </svg>
                                }
                                title="Memory"
                                value={database.metrics.memory.percent}
                                unit="%"
                                subtitle={`${database.metrics.memory.used_gb} / ${database.metrics.memory.total_gb} GB`}
                                percent={database.metrics.memory.percent}
                                color="success"
                            />
                        </Col>
                        <Col xs={6} lg={3}>
                            <MetricCard
                                icon={
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                                        <ellipse cx="12" cy="5" rx="9" ry="3" />
                                        <path d="M21 12c0 1.66-4.03 3-9 3s-9-1.34-9-3" />
                                        <path d="M3 5v14c0 1.66 4.03 3 9 3s9-1.34 9-3V5" />
                                    </svg>
                                }
                                title="Storage"
                                value={database.metrics.disk.percent}
                                unit="%"
                                subtitle={`${database.metrics.disk.used_gb} / ${database.metrics.disk.total_gb} GB`}
                                percent={database.metrics.disk.percent}
                                color="warning"
                            />
                        </Col>
                        <Col xs={6} lg={3}>
                            <MetricCard
                                icon={
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                                        <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
                                    </svg>
                                }
                                title="Connections"
                                value={database.metrics.connections.total}
                                unit={`/ ${database.metrics.connections.max}`}
                                subtitle={`${database.metrics.connections.active} active, ${database.metrics.connections.idle} idle`}
                                percent={(database.metrics.connections.total / database.metrics.connections.max) * 100}
                                color="info"
                            />
                        </Col>
                    </Row>

                    {/* Tabs */}
                    <div className="db-detail-tabs">
                        <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
                            <Nav variant="tabs" className="db-nav-tabs">
                                <Nav.Item>
                                    <Nav.Link eventKey="overview">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <ellipse cx="12" cy="5" rx="9" ry="3" />
                                            <path d="M3 5v7c0 1.66 4.03 3 9 3s9-1.34 9-3V5" />
                                            <path d="M3 12v7c0 1.66 4.03 3 9 3s9-1.34 9-3v-7" />
                                        </svg>
                                        Overview
                                    </Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link eventKey="connectivity">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <circle cx="12" cy="12" r="10" />
                                            <line x1="2" y1="12" x2="22" y2="12" />
                                            <path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
                                        </svg>
                                        Connectivity
                                    </Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link eventKey="configuration">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <circle cx="12" cy="12" r="3" />
                                            <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z" />
                                        </svg>
                                        Configuration
                                    </Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link eventKey="backups">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                                            <polyline points="17 8 12 3 7 8" />
                                            <line x1="12" y1="3" x2="12" y2="15" />
                                        </svg>
                                        Backups
                                    </Nav.Link>
                                </Nav.Item>
                            </Nav>

                            <Tab.Content className="db-tab-content">
                                {/* Overview Tab */}
                                <Tab.Pane eventKey="overview">
                                    <Row>
                                        <Col lg={6}>
                                            <div className="info-section">
                                                <h6 className="info-section-title">Database Information</h6>
                                                <table className="db-info-table">
                                                    <tbody>
                                                        <InfoRow label="Database Engine" value={`${getEngineDisplayName(database.type)} ${database.version}`} />
                                                        <InfoRow label="Instance ID" value={database.id} copyable onCopy={handleCopy} />
                                                        <InfoRow label="Database Name" value={database.database} />
                                                        <InfoRow label="Status" value={<StatusBadge status={database.status} />} />
                                                        <InfoRow label="Environment" value={<Badge bg="secondary">{database.environment}</Badge>} />
                                                    </tbody>
                                                </table>
                                            </div>
                                        </Col>
                                        <Col lg={6}>
                                            <div className="info-section">
                                                <h6 className="info-section-title">Resource Summary</h6>
                                                <table className="db-info-table">
                                                    <tbody>
                                                        <InfoRow label="vCPU Cores" value={`${database.cpuCores} cores`} />
                                                        <InfoRow label="Memory" value={`${database.ramGb} GB`} />
                                                        <InfoRow label="Storage" value={database.storage} />
                                                        <InfoRow label="Max Connections" value={database.maxConnections} />
                                                        <InfoRow label="Uptime" value={database.metrics.uptime} />
                                                        <InfoRow label="Created" value={moment(database.createdAt).format("MMM DD, YYYY HH:mm")} />
                                                        <InfoRow label="Last Updated" value={moment(database.updatedAt).format("MMM DD, YYYY HH:mm")} />
                                                    </tbody>
                                                </table>
                                            </div>
                                        </Col>
                                    </Row>
                                </Tab.Pane>

                                {/* Connectivity Tab */}
                                <Tab.Pane eventKey="connectivity">
                                    <Row>
                                        <Col lg={6}>
                                            <div className="info-section">
                                                <h6 className="info-section-title">Endpoint & Port</h6>
                                                <table className="db-info-table">
                                                    <tbody>
                                                        <InfoRow
                                                            label="Production Endpoint"
                                                            value={database.endpoints.production?.host || "Pending..."}
                                                            copyable
                                                            onCopy={handleCopy}
                                                        />
                                                        <InfoRow label="Port" value={database.endpoints.production?.port || database.port || defaultPorts[database.type?.toLowerCase()] || 1433} />
                                                        <InfoRow label="Database" value={database.database} />
                                                        <InfoRow label="Username" value={database.username} copyable onCopy={handleCopy} />
                                                    </tbody>
                                                </table>
                                            </div>

                                            <div className="info-section mt-4">
                                                <h6 className="info-section-title">Connection Strings</h6>
                                                <div className="connection-string-item">
                                                    <label>Standard Connection</label>
                                                    <div className="connection-string-box-small">
                                                        <code>{database.connectionString}</code>
                                                        <button onClick={() => handleCopy(database.connectionString)}>
                                                            <CopyIcon size={14} />
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className="connection-string-item">
                                                    <label>SSL Connection</label>
                                                    <div className="connection-string-box-small">
                                                        <code>{database.sslConnectionString}</code>
                                                        <button onClick={() => handleCopy(database.sslConnectionString)}>
                                                            <CopyIcon size={14} />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </Col>
                                        <Col lg={6}>
                                            <div className="info-section">
                                                <h6 className="info-section-title">Connection Statistics</h6>
                                                <table className="db-info-table">
                                                    <tbody>
                                                        <InfoRow label="Active Connections" value={<Badge bg="success">{database.metrics.connections.active}</Badge>} />
                                                        <InfoRow label="Idle Connections" value={<Badge bg="secondary">{database.metrics.connections.idle}</Badge>} />
                                                        <InfoRow label="Total Connections" value={<strong>{database.metrics.connections.total}</strong>} />
                                                        <InfoRow label="Max Connections" value={database.metrics.connections.max} />
                                                        <InfoRow
                                                            label="Available"
                                                            value={<span className="text-success">{database.metrics.connections.max - database.metrics.connections.total}</span>}
                                                        />
                                                    </tbody>
                                                </table>
                                            </div>


                                        </Col>
                                    </Row>
                                </Tab.Pane>

                                {/* Configuration Tab */}
                                <Tab.Pane eventKey="configuration">
                                    <Row>
                                        <Col lg={6}>
                                            <div className="info-section">
                                                <h6 className="info-section-title">Instance Configuration</h6>
                                                <table className="db-info-table">
                                                    <tbody>
                                                        <InfoRow label="Instance Tier" value={database.tier} />
                                                        <InfoRow label="vCPU" value={`${database.cpuCores} cores`} />
                                                        <InfoRow label="Memory" value={`${database.ramGb} GB`} />
                                                        <InfoRow label="Storage Size" value={database.storage} />
                                                        <InfoRow label="Storage Type" value="SSD (gp3)" />
                                                        <InfoRow label="IOPS" value="3000" />
                                                    </tbody>
                                                </table>
                                            </div>
                                        </Col>
                                        <Col lg={6}>
                                            <div className="info-section">
                                                <h6 className="info-section-title">{getEngineDisplayName(database.type)} Settings</h6>
                                                <table className="db-info-table">
                                                    <tbody>
                                                        <InfoRow label="Max Connections" value={database.maxConnections} />
                                                        <InfoRow label="Shared Buffers" value="1GB" />
                                                        <InfoRow label="Work Mem" value="64MB" />
                                                        <InfoRow label="Maintenance Work Mem" value="512MB" />
                                                        <InfoRow label="Effective Cache Size" value="3GB" />
                                                        <InfoRow label="SSL Enabled" value={<Badge bg="success">Yes</Badge>} />
                                                    </tbody>
                                                </table>
                                            </div>
                                        </Col>
                                    </Row>
                                </Tab.Pane>

                                {/* Backups Tab */}
                                <Tab.Pane eventKey="backups">
                                    <Row>
                                        <Col lg={6}>
                                            <div className="info-section">
                                                <h6 className="info-section-title">Backup Configuration</h6>
                                                <table className="db-info-table">
                                                    <tbody>
                                                        <InfoRow
                                                            label="Automated Backups"
                                                            value={database.backupEnabled ? <Badge bg="success">Enabled</Badge> : <Badge bg="secondary">Disabled</Badge>}
                                                        />
                                                        <InfoRow label="Backup Schedule" value={database.backupSchedule} />
                                                        <InfoRow label="Retention Period" value={database.backupRetention} />
                                                        <InfoRow label="Last Backup" value={moment(database.metrics.lastBackup).format("MMM DD, YYYY HH:mm")} />
                                                    </tbody>
                                                </table>
                                            </div>
                                        </Col>
                                        <Col lg={6}>
                                            <div className="info-section">
                                                <h6 className="info-section-title">Recent Backups</h6>
                                                <div className="backup-list">
                                                    <div className="backup-item">
                                                        <div className="backup-info">
                                                            <span className="backup-name">backup-2026-04-16-0600</span>
                                                            <span className="backup-date">Apr 16, 2026 06:00 UTC</span>
                                                        </div>
                                                        <div className="backup-actions">
                                                            <Badge bg="success">Completed</Badge>
                                                            <span className="backup-size">2.3 GB</span>
                                                        </div>
                                                    </div>
                                                    <div className="backup-item">
                                                        <div className="backup-info">
                                                            <span className="backup-name">backup-2026-04-15-0600</span>
                                                            <span className="backup-date">Apr 15, 2026 06:00 UTC</span>
                                                        </div>
                                                        <div className="backup-actions">
                                                            <Badge bg="success">Completed</Badge>
                                                            <span className="backup-size">2.2 GB</span>
                                                        </div>
                                                    </div>
                                                    <div className="backup-item">
                                                        <div className="backup-info">
                                                            <span className="backup-name">backup-2026-04-14-0600</span>
                                                            <span className="backup-date">Apr 14, 2026 06:00 UTC</span>
                                                        </div>
                                                        <div className="backup-actions">
                                                            <Badge bg="success">Completed</Badge>
                                                            <span className="backup-size">2.1 GB</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                </Tab.Pane>
                            </Tab.Content>
                        </Tab.Container>
                    </div>
                </Container>
            </div>

            {/* Delete Confirmation Modal */}
            <Modal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                centered
                className="delete-modal"
                backdrop="static"
            >
                <Modal.Body>
                    <div className="text-center">
                        <img src={bin} alt="bin" className="modal-delete-icon" />
                        <h4>Delete Database?</h4>
                        <p>
                            Are you sure you want to delete <strong>"{database.name}"</strong>?
                            <br />This action cannot be undone and all data will be permanently lost.
                        </p>
                        <div className="d-flex align-items-center gap-2 justify-content-center">
                            <button
                                className="grey-btn"
                                onClick={() => setShowDeleteModal(false)}
                            >
                                No, Keep
                            </button>
                            <button
                                className="red-btn"
                                onClick={handleDelete}
                            >
                                Yes, Delete
                            </button>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>

            {/* Password Confirmation Modal for Production Databases */}
            <Modal
                show={showPasswordModal}
                onHide={() => { setShowPasswordModal(false); setDeletePassword(""); }}
                centered
                className="delete-modal"
                backdrop="static"
            >
                <Modal.Body>
                    <div className="text-center">
                        <img src={bin} alt="bin" className="modal-delete-icon" />
                        <h4>Production Database</h4>
                        <p>
                            Please enter your password to confirm deletion of <strong>"{database.name}"</strong>.
                        </p>
                        <div className="mb-3">
                            <input
                                type="password"
                                className="form-control"
                                placeholder="Enter your password"
                                value={deletePassword}
                                onChange={(e) => setDeletePassword(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && handlePasswordDelete()}
                                autoFocus
                            />
                        </div>
                        <div className="d-flex align-items-center gap-2 justify-content-center">
                            <button
                                className="grey-btn"
                                onClick={() => { setShowPasswordModal(false); setDeletePassword(""); }}
                                disabled={deleteLoading}
                            >
                                Cancel
                            </button>
                            <button
                                className="red-btn"
                                onClick={handlePasswordDelete}
                                disabled={deleteLoading || !deletePassword.trim()}
                            >
                                {deleteLoading ? (
                                    <Spinner animation="border" size="sm" />
                                ) : (
                                    "Delete"
                                )}
                            </button>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>

            {/* Stop Confirmation Modal */}
            <Modal
                show={showStopModal}
                onHide={() => setShowStopModal(false)}
                centered
                className="delete-modal stop-modal"
                backdrop="static"
            >
                <Modal.Body>
                    <div className="text-center">
                        <div className="modal-stop-icon">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#e8586d" strokeWidth="2">
                                <circle cx="12" cy="12" r="10" />
                                <line x1="12" y1="8" x2="12" y2="12" />
                                <line x1="12" y1="16" x2="12.01" y2="16" />
                            </svg>
                        </div>
                        <h4>Stop Database?</h4>
                        <p>
                            Are you sure you want to stop <strong>"{database.name}"</strong>?
                            <br />Stopping the database will disconnect all active connections.
                        </p>
                        <div className="d-flex align-items-center gap-2 justify-content-center">
                            <button
                                className="grey-btn"
                                onClick={() => setShowStopModal(false)}
                            >
                                Cancel
                            </button>
                            <button
                                className="red-btn stop-btn"
                                onClick={handleConfirmStop}
                            >
                                Yes, Stop
                            </button>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>
        </div>
    );
}

export default DatabaseDetail;
